import { useLayoutEffect, useRef, useState } from "react";

type UseTableBodyHeightParams = {
  selectedYear: number | undefined;
  // length of the data backing the currently-selected year, so the effect re-measures once that data (and thus
  // the table's header/rows) actually renders, rather than only on the initial (empty) render
  tableDataLength: number;
  // whether the table wrapper is currently unmounted (e.g. a loading skeleton is shown in its place) - the
  // effect re-runs when this flips to false, since that's when the wrapper actually enters the DOM
  isWrapperHidden: boolean;
};

// measures the table body height (wrapper height minus its own header height) so the table can scroll
// internally instead of the page, and stamps --historical-practice-page-height (viewport minus app header) for
// CSS consumers - shared by historical-practice-data-table-view and its all-fields-data-table-view sibling
export const useTableBodyHeight = ({ selectedYear, tableDataLength, isWrapperHidden }: UseTableBodyHeightParams) => {
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const [tableBodyHeight, setTableBodyHeight] = useState(0);

  useLayoutEffect(() => {
    const wrapper = tableWrapperRef.current;
    if (!wrapper) return undefined;

    const getHeight = (element: Element | null) => (element instanceof HTMLElement ? element.getBoundingClientRect().height : 0);

    const updateTableHeight = () => {
      const headerElement = wrapper.querySelector(".ant-table-header") || wrapper.querySelector(".ant-table-thead");
      const headerHeight = getHeight(headerElement);
      const wrapperHeight = getHeight(wrapper);
      const pageContainer = wrapper.closest(".historical-practice-data-table-view-page");
      const appHeaderHeight = getHeight(document.querySelector(".ant-layout-header"));
      const pageHeight = Math.max(window.innerHeight - appHeaderHeight, 0);
      const nextBodyHeight = Math.max(wrapperHeight - headerHeight, 0);
      setTableBodyHeight((prev) => (prev === nextBodyHeight ? prev : nextBodyHeight));
      if (pageContainer instanceof HTMLElement) {
        pageContainer.style.setProperty("--historical-practice-page-height", `${pageHeight}px`);
      }
    };

    const resizeObserver = new ResizeObserver(updateTableHeight);
    resizeObserver.observe(wrapper);
    const headerElement = wrapper.querySelector(".ant-table-header") || wrapper.querySelector(".ant-table-thead");
    if (headerElement instanceof HTMLElement) resizeObserver.observe(headerElement);

    updateTableHeight();

    return () => resizeObserver.disconnect();
    // re-run when table becomes visible (skeleton → table): wrapper is only in DOM when isWrapperHidden is false.
  }, [selectedYear, tableDataLength, isWrapperHidden]);

  return { tableWrapperRef, tableBodyHeight };
};
